import { getAllSubscriptions } from './subscriptionService';
import { getCollection } from './firestoreService';
import { getAnalyticsEvents } from './analyticsService';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { app } from '../config/firebase';
import { format, subDays, differenceInDays, startOfMonth } from 'date-fns';

const functions = getFunctions(app);

export interface RevenueByPlanType {
  planId: string;
  revenue: number;
  count: number;
}

export interface RevenueByCohort {
  cohort: string;
  revenue: number;
  users: number;
}

export interface PaymentFailureMetrics {
  totalFailures: number;
  recoveryRate: number;
  failureReasons: Map<string, number>;
}

export interface SubscriptionLifecycle {
  averageDuration: number;
  cancellationRate: number;
  upgradeRate: number;
  downgradeRate: number;
  trialConversionRate: number;
}

export interface RevenueDeepDiveData {
  revenueByPlanType: RevenueByPlanType[];
  revenueByCohort: RevenueByCohort[];
  paymentFailureMetrics: PaymentFailureMetrics;
  subscriptionLifecycle: SubscriptionLifecycle;
  refundRate: number;
  totalRefunds: number;
  trialConversionBySource: Map<string, number>;
  ltvByCohort: Map<string, number>;
}

export const getRevenueDeepDiveAnalytics = async (
  days: number = 90
): Promise<RevenueDeepDiveData> => {
  try {
    const subscriptions = await getAllSubscriptions();
    const users = await getCollection('users', undefined, undefined, undefined, 10000);
    const events = await getAnalyticsEvents(days);

    // Revenue by plan type
    const planRevenueMap = new Map<string, { revenue: number; count: number }>();
    subscriptions.forEach((sub: any) => {
      if (sub.status === 'active' && !sub.isTrial) {
        const planId = sub.planId || 'unknown';
        const existing = planRevenueMap.get(planId) || { revenue: 0, count: 0 };
        planRevenueMap.set(planId, {
          revenue: existing.revenue + (sub.amount || 0),
          count: existing.count + 1,
        });
      }
    });

    const revenueByPlanType: RevenueByPlanType[] = Array.from(planRevenueMap.entries())
      .map(([planId, data]) => ({
        planId,
        revenue: data.revenue,
        count: data.count,
      }))
      .sort((a, b) => b.revenue - a.revenue);

    // Revenue by cohort
    const cohortMap = new Map<string, { revenue: number; users: Set<string> }>();
    users.forEach((user: any) => {
      const signupDate = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
      const cohortKey = format(startOfMonth(signupDate), 'yyyy-MM');
      
      const userSubs = subscriptions.filter((s: any) => s.userId === user.id && s.status === 'active');
      const userRevenue = userSubs.reduce((sum: number, s: any) => sum + (s.amount || 0), 0);
      
      if (!cohortMap.has(cohortKey)) {
        cohortMap.set(cohortKey, { revenue: 0, users: new Set() });
      }
      const cohort = cohortMap.get(cohortKey)!;
      cohort.revenue += userRevenue;
      cohort.users.add(user.id);
    });

    const revenueByCohort: RevenueByCohort[] = Array.from(cohortMap.entries())
      .map(([cohort, data]) => ({
        cohort,
        revenue: data.revenue,
        users: data.users.size,
      }))
      .sort((a, b) => a.cohort.localeCompare(b.cohort));

    // Payment failure metrics
    const paymentFailures = events.filter(
      (e) => e.eventName === 'payment_failure' || e.eventName.includes('payment_fail')
    );
    const paymentRecoveries = events.filter(
      (e) => e.eventName === 'payment_recovery' || e.eventName.includes('payment_recovery')
    );

    const failureReasons = new Map<string, number>();
    paymentFailures.forEach((event) => {
      const reason = (event.properties.reason as string) || (event.properties.error_message as string) || 'Unknown';
      failureReasons.set(reason, (failureReasons.get(reason) || 0) + 1);
    });

    const recoveryRate = paymentFailures.length > 0
      ? (paymentRecoveries.length / paymentFailures.length) * 100
      : 0;

    // Subscription lifecycle
    const activeSubs = subscriptions.filter((s: any) => s.status === 'active');
    const canceledSubs = subscriptions.filter((s: any) => s.status === 'canceled' || s.status === 'expired');
    const trialSubs = subscriptions.filter((s: any) => s.isTrial === true);
    
    // Calculate average duration (simplified)
    let totalDuration = 0;
    let durationCount = 0;
    canceledSubs.forEach((sub: any) => {
      if (sub.createdAt && sub.updatedAt) {
        const created = sub.createdAt instanceof Date ? sub.createdAt : new Date(sub.createdAt);
        const updated = sub.updatedAt instanceof Date ? sub.updatedAt : new Date(sub.updatedAt);
        const days = differenceInDays(updated, created);
        totalDuration += days;
        durationCount++;
      }
    });
    const averageDuration = durationCount > 0 ? totalDuration / durationCount : 0;

    const cancellationRate = subscriptions.length > 0
      ? (canceledSubs.length / subscriptions.length) * 100
      : 0;

    // Trial conversion
    const convertedTrials = subscriptions.filter((s: any) => s.isTrial === false && s.status === 'active');
    const trialConversionRate = trialSubs.length > 0
      ? (convertedTrials.length / trialSubs.length) * 100
      : 0;

    // Trial conversion by source (if tracked)
    const trialConversionBySource = new Map<string, number>();
    events.forEach((event) => {
      if (event.eventName === 'subscription_started' || event.eventName.includes('trial')) {
        const source = (event.properties.source as string) || 'unknown';
        trialConversionBySource.set(source, (trialConversionBySource.get(source) || 0) + 1);
      }
    });

    // LTV by cohort
    const ltvByCohort = new Map<string, number>();
    cohortMap.forEach((cohort, cohortKey) => {
      const avgRevenuePerUser = cohort.users.size > 0 ? cohort.revenue / cohort.users.size : 0;
      // Estimate LTV as 12 months of revenue (simplified)
      ltvByCohort.set(cohortKey, avgRevenuePerUser * 12);
    });

    // Refunds (from Stripe if available)
    let refundRate = 0;
    let totalRefunds = 0;
    try {
      const getStripeCharges = httpsCallable(functions, 'adminStripeProxy');
      const result = await getStripeCharges({
        action: 'listCharges',
        limit: 100,
      });
      
      const charges = (result.data as any)?.charges || [];
      const refundedCharges = charges.filter((c: any) => c.refunded === true || c.amount_refunded > 0);
      totalRefunds = refundedCharges.reduce((sum: number, c: any) => sum + ((c.amount_refunded || 0) / 100), 0);
      refundRate = charges.length > 0 ? (refundedCharges.length / charges.length) * 100 : 0;
    } catch (error) {
      console.warn('Could not fetch refund data from Stripe:', error);
    }

    return {
      revenueByPlanType,
      revenueByCohort,
      paymentFailureMetrics: {
        totalFailures: paymentFailures.length,
        recoveryRate,
        failureReasons,
      },
      subscriptionLifecycle: {
        averageDuration,
        cancellationRate,
        upgradeRate: 0, // Would need upgrade tracking
        downgradeRate: 0, // Would need downgrade tracking
        trialConversionRate,
      },
      refundRate,
      totalRefunds,
      trialConversionBySource,
      ltvByCohort,
    };
  } catch (error) {
    console.error('Error calculating revenue deep dive analytics:', error);
    throw error;
  }
};

